import {HttpClient, HttpContext} from '@angular/common/http'
import {inject, Injectable} from '@angular/core'
import {filter, Observable, switchMap} from 'rxjs'
import {CACHE_FORCE_REFRESH, CACHING_DEFAULT_TTL, CACHING_ENABLED, CACHING_TTL} from '../config/http.config'
import {APIRequestOptions, APIResponse} from '../interfaces'
import {APIRequestResource} from '../types'
import {generateUrl} from '../util'
import {TokenService} from './token.service'

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    private readonly http = inject(HttpClient)
    private readonly tokenService = inject(TokenService)

    readonly GET = <T>(
        resource: APIRequestResource,
        options: APIRequestOptions,
        caching = false,
        forceRefresh = false,
        cachingTTL = CACHING_DEFAULT_TTL
    ) => {
        return this.tokenService.isRefreshing$.pipe(
            filter((isRefreshing) => !isRefreshing),
            switchMap(() =>
                this.http.get<APIResponse<T>>(generateUrl(resource, options), {
                    params: options.params,
                    context: new HttpContext().set(CACHING_ENABLED, caching).set(CACHING_TTL, cachingTTL).set(CACHE_FORCE_REFRESH, forceRefresh),
                })
            )
        )
    }

    readonly DELETE = <T>(
        resource: APIRequestResource,
        data: unknown,
        options: APIRequestOptions,
        caching = false,
        forceRefresh = false,
        cachingTTL = CACHING_DEFAULT_TTL
    ) => {
        return this.tokenService.isRefreshing$.pipe(
            filter((isRefreshing) => !isRefreshing),
            switchMap(() =>
                this.http.delete<APIResponse<T>>(generateUrl(resource, options), {
                    params: options.params,
                    body: data,
                    context: new HttpContext().set(CACHING_ENABLED, caching).set(CACHING_TTL, cachingTTL).set(CACHE_FORCE_REFRESH, forceRefresh),
                })
            )
        )
    }

    readonly PATCH = <T>(
        resource: APIRequestResource,
        data: unknown,
        options: APIRequestOptions,
        caching = false,
        forceRefresh = false,
        cachingTTL = CACHING_DEFAULT_TTL
    ) => {
        return this.tokenService.isRefreshing$.pipe(
            filter((isRefreshing) => !isRefreshing),
            switchMap(() =>
                this.http.patch<APIResponse<T>>(generateUrl(resource, options), data, {
                    params: options.params,
                    context: new HttpContext().set(CACHING_ENABLED, caching).set(CACHING_TTL, cachingTTL).set(CACHE_FORCE_REFRESH, forceRefresh),
                })
            )
        )
    }

    readonly POST = <T>(
        resource: APIRequestResource,
        data: unknown,
        options: APIRequestOptions,
        caching = false,
        forceRefresh = false,
        cachingTTL = CACHING_DEFAULT_TTL
    ) => {
        return this.tokenService.isRefreshing$.pipe(
            filter((isRefreshing) => !isRefreshing),
            switchMap(() =>
                this.http.post<APIResponse<T>>(generateUrl(resource, options), data, {
                    params: options.params,
                    context: new HttpContext().set(CACHING_ENABLED, caching).set(CACHING_TTL, cachingTTL).set(CACHE_FORCE_REFRESH, forceRefresh),
                })
            )
        )
    }

    readonly PUT = <T>(
        resource: APIRequestResource,
        data: unknown,
        options: APIRequestOptions,
        caching = false,
        forceRefresh = false,
        cachingTTL = CACHING_DEFAULT_TTL
    ) => {
        return this.tokenService.isRefreshing$.pipe(
            filter((isRefreshing) => !isRefreshing),
            switchMap(() =>
                this.http.put<APIResponse<T>>(generateUrl(resource, options), data, {
                    params: options.params,
                    context: new HttpContext().set(CACHING_ENABLED, caching).set(CACHING_TTL, cachingTTL).set(CACHE_FORCE_REFRESH, forceRefresh),
                })
            )
        )
    }

    readonly DOWNLOAD_BLOB = (
        resource: APIRequestResource,
        options: APIRequestOptions,
        fileName: string,
        caching = false,
        forceRefresh = false,
        cachingTTL = CACHING_DEFAULT_TTL
    ) => {
        return this.tokenService.isRefreshing$.pipe(
            filter((isRefreshing) => !isRefreshing),
            switchMap(() =>
                this.http.get(generateUrl(resource, options), {
                    params: options.params,
                    responseType: 'blob',
                    context: new HttpContext()
                        .set(CACHING_ENABLED, caching)
                        .set(CACHING_TTL, cachingTTL)
                        .set(CACHE_FORCE_REFRESH, forceRefresh),
                })
            )
        );
    };


    /**
     * Subscribe to a Server-Sent Events (SSE) endpoint.
     * Keeps listening until the stream ends or you unsubscribe.
     */
    readonly SSE = <T = any>(resource: APIRequestResource,options: APIRequestOptions): Observable<T> => {
        return new Observable<T>((observer) => {
            const eventSource = new EventSource(generateUrl(resource, options), {withCredentials: true})

            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    observer.next(data)
                } catch {
                    observer.next(event.data as any)
                }
            }

            eventSource.onerror = (error) => {
                observer.error(error)
                eventSource.close()
            }

            return () => eventSource.close()
        })
    }
}
